import type { ColumnResizeMode, Header } from '@tanstack/react-table';
import classNames from 'classnames';
import React from 'react';
import type { ElementProps } from '../element/Element';
import { Element } from '../element/Element';
import Icon from '../Icon';
import styles from './Table.module.scss';

interface Props extends ElementProps {}

export type TableProps = {
  /** Adds border & border-radius to table */
  standalone?: boolean;
  /** Adds coloring of every other row */
  zebraStripes?: boolean;
  /** Shows vertical separators between cells */
  verticalBorders?: boolean;
};

export type CellProps = {
  /** Cells (`td` & `th` with large content -- e.g avatar, need more vertical room so apply tallContent) */
  tallContent?: boolean;
  /** Numeric values needs to be right aligned and fixed in width */
  numeric?: boolean;
  /** Right align the value (but not fixed width, like `numeric`) */
  alignRight?: boolean;
  /** Pin this column to the left? */
  pinnedLeft?: boolean;
  /** Pin this column to the right? */
  pinnedRight?: boolean;
  /** Highlight this column with a background color */
  highlight?: boolean;
};

/**
 * Table Component /w all the subcomponents
 * Generic and basic for building blocks.
 */
const TableContainer = (props: Props & TableProps) => {
  const { standalone, zebraStripes, verticalBorders, ...rest } = props;
  return (
    <Element
      as="table"
      {...rest}
      className={classNames(props.className, styles.table, {
        [styles.standalone]: standalone,
        [styles.zebraStripes]: zebraStripes,
        [styles.verticalBorders]: verticalBorders,
      })}
    />
  );
};

type TheadProps = Props & {
  sticky?: boolean;
};
const Thead = (props: TheadProps) => {
  const { sticky, ...rest } = props;
  return (
    <Element
      as="thead"
      {...rest}
      className={classNames(props.className, styles.thead, {
        [styles.sticky]: sticky,
      })}
    />
  );
};

type TFootProps = Props & {
  sticky?: boolean;
};

const Tfoot = ({ sticky, ...rest }: TFootProps) => {
  return (
    <Element
      as="tfoot"
      {...rest}
      className={classNames(rest.className, styles.tfoot, {
        [styles.sticky]: sticky,
      })}
    />
  );
};

type TableRowProps = Props & {
  isClickable?: boolean;
  isSelected?: boolean;
  highlight?: boolean;
};

const Tr = (props: TableRowProps) => {
  const { isClickable, isSelected, highlight, ...rest } = props;
  return (
    <Element
      as="tr"
      {...rest}
      className={classNames(props.className, styles.tr, {
        [styles.isClickable]: isClickable,
        [styles.isSelected]: isSelected,
        [styles.highlight]: highlight,
      })}
      data-selected={isSelected}
    />
  );
};

type ThCellProps = Props &
  CellProps & {
    sortable?: boolean;
    sorted?: boolean;
  };

const Th = (props: ThCellProps) => {
  const {
    tallContent,
    numeric,
    alignRight,
    sortable,
    sorted,
    pinnedLeft,
    pinnedRight,
    highlight,
    ...rest
  } = props;

  return (
    <Element
      as="th"
      {...rest}
      className={classNames(props.className, styles.th, {
        [styles.tallContent]: tallContent,
        [styles.numeric]: numeric,
        [styles.alignRight]: alignRight,
        [styles.sortable]: sortable,
        [styles.sorted]: sorted,
        [styles.pinnedLeft]: pinnedLeft,
        [styles.pinnedRight]: pinnedRight,
        [styles.highlight]: highlight,
      })}
    />
  );
};

const Td = (props: Props & CellProps) => {
  const { tallContent, numeric, alignRight, pinnedLeft, pinnedRight, highlight, ...rest } = props;
  return (
    <Element
      as="td"
      {...rest}
      className={classNames(props.className, styles.td, {
        [styles.tallContent]: tallContent,
        [styles.numeric]: numeric,
        [styles.alignRight]: alignRight,
        [styles.pinnedLeft]: pinnedLeft,
        [styles.pinnedRight]: pinnedRight,
        [styles.highlight]: highlight,
      })}
    />
  );
};

const Tbody = (props: Props) => {
  return <Element as="tbody" {...props} className={classNames(props.className, styles.tbody)} />;
};

const Caption = (props: Props) => {
  return (
    <Element as="caption" {...props} className={classNames(props.className, styles.caption)} />
  );
};

const Colgroup = (props: Props) => {
  return (
    <Element as="colgroup" {...props} className={classNames(props.className, styles.colgroup)} />
  );
};

/**
 * Special Table "Decorators"
 */

type ThResizerProps = Omit<Props, 'children'> & {
  header: Header<any, unknown>;
  columnResizeMode?: ColumnResizeMode;
  deltaOffset: number | null;
};
const ThResizer = ({ header, columnResizeMode, deltaOffset, ...rest }: ThResizerProps) => {
  return (
    <div
      {...rest}
      {...{
        onMouseDown: header.getResizeHandler(),
        onTouchStart: header.getResizeHandler(),
        className: classNames(styles.resizer, {
          [styles.isResizing]: header.column.getIsResizing(),
        }),
        style: {
          transform:
            columnResizeMode === 'onEnd' && header.column.getIsResizing()
              ? `translateX(${deltaOffset}px)`
              : '',
        },
      }}
    />
  );
};
type ThSorterProps = Props & {
  header: Header<any, unknown>;
};

const ThSorter = ({ header, children }: ThSorterProps) => {
  return (
    <div>
      {children}
      {{
        asc: <Icon name="ArrowNarrowUp" data-chevron width={16} height={16} />,
        desc: <Icon name="ArrowNarrowDown" data-chevron width={16} height={16} />,
      }[header.column.getIsSorted() as string] ?? null}
    </div>
  );
};

interface ThDefaultProps<T = unknown> {
  header: Header<T, unknown>;
  columnResizeMode?: ColumnResizeMode;
  deltaOffset?: number | null;
  children: React.ReactNode | string;
  leftOffset?: number;
  rightOffset?: number;
}

const ThDefault = <T,>({
  header,
  columnResizeMode = 'onChange',
  deltaOffset = null,
  children,
  leftOffset,
  rightOffset,
}: ThDefaultProps<T>) => {
  const isPinned = header.column.getIsPinned();
  return (
    <Table.Th
      key={header.id}
      sortable={header.column.getCanSort()}
      sorted={!!header.column.getIsSorted()}
      pinnedLeft={isPinned === 'left'}
      pinnedRight={isPinned === 'right'}
      onClick={header.column.getToggleSortingHandler()}
      {...{
        colSpan: header.colSpan,
        style: {
          width: header.getSize(),
          left: leftOffset,
          right: rightOffset,
        },
      }}
    >
      {header.isPlaceholder ? null : <Table.ThSorter header={header}>{children}</Table.ThSorter>}
      {header.column.getCanResize() && (
        <Table.ThResizer
          header={header}
          columnResizeMode={columnResizeMode}
          deltaOffset={deltaOffset}
        />
      )}
    </Table.Th>
  );
};

/**
 * `<table>` wrapper component
 */
export const Table = Object.assign(TableContainer, {
  /** `<thead>` wrapper */
  Thead,
  /** `<tbody>` wrapper */
  Tbody,
  /** `<tfoot>` wrapper */
  Tfoot,
  /** `<caption>` wrapper */
  Caption,
  /** `<colgroup>` wrapper */
  Colgroup,
  /** `<tr>` wrapper */
  Tr,
  /** `<td>` wrapper */
  Td,
  /** `<th>` wrapper */
  Th,
  /** Resize Handle for Th */
  ThResizer,
  /** Wrap `Th` contents for sorting */
  ThSorter,
  /** Default Th cell */
  ThDefault,
});
